export interface TdspIOFlights {
  start_date;
  end_date;
  disable_start_date: boolean;
  disable_end_date: boolean;
  revenue_budget: number;
  impression_budget: number;
  daily_revenue_budget: number;
  daily_imps_budget: number;
  description: string;
  click_pacing_daily_minimum_budget: number;
  click_pacing_flight_budget: number;
  click_pacing_average_daily_budget?: number;
  click_pacing_clicks_delivered?: number;
}

export interface Goal {
  goal_type: string;
  value;
}

export interface TdspIO {
  name: string;
  state: "active" | "inactive";
  io_type: "regular" | "private_deal";
  io_deal_type: "open_rtb" | "pg" | "pmp" | "pub_direct";
  budget_type: "revenue" | "impression";
  pacing: "pace_daily" | "pace_even" | "pace_ahead" | "pace_asap";
  selectedKpis: Array<any>;
  enable_click_pacing: boolean;
  flights: Array<TdspIOFlights>;
  auto_allocate_budgets: boolean;
  primary_goal: Goal;
  secondary_goal: Goal;
  tertiary_goal: Goal;
  pixel_mappings: Array<any>;
  image_pixels: Array<any>;
  seed: number | string;
  ad_type:
    | "banner"
    | "video"
    | "audio"
    | "ctv"
    | "native_banner"
    | "native_video"
    | "dooh_banner"
    | "dooh_video";
  max_cpm: number;
  cpc: number;
  sc_commission?: number | null;
  sdc_commission?: number | null;
}

export interface TdspIoAssoc {
  seed: Array<any>;
  tracking_tags: Array<any>;
}

export interface isIOValid {
  basic_setup: boolean;
  seed_setup: boolean;
  cp_setup: boolean;
}

export const budgetTypeOptions: Array<any> = [
  { label: "Revenue", value: "revenue" },
  { label: "Impression", value: "impression" },
];

export type IoFieldOption = {
  label: string;
  value: string;
  toolTip: string;
};

export const tdspPacingOptions: ReadonlyArray<IoFieldOption> = [
  {
    label: "Pace to Daily Cap",
    value: "pace_daily",
    toolTip: "The campaign will aim to pace to the specified daily spend cap.",
  },
  {
    label: "Pace Evenly",
    value: "pace_even",
    toolTip:
      "The campaign will aim to spend the allotted budgets evenly throughout the flight dates.",
  },
  {
    label: "Pace Ahead",
    value: "pace_ahead",
    toolTip:
      "The campaign will aim to spend 20% above the allotted budget on a day-to-day basis.",
  },
  // {
  //   label: "Pace as Soon as Possible",
  //   value: "pace_asap",
  //   toolTip: "The campaign will pace as soon as possible within minutes.",
  // },
];

export const tdspAdTypes: Array<IoFieldOption> = [
  { label: "Banner/HTML5", value: "banner", toolTip: "" },
  { label: "Video", value: "video", toolTip: "" },
  { label: "Audio", value: "audio", toolTip: "" },
  { label: "Connected TV", value: "ctv", toolTip: "" },
  { label: "Native Banner", value: "native_banner", toolTip: "" },
  { label: "Native Video", value: "native_video", toolTip: "" },
  // {
  //   value: "dooh_banner",
  //   label: "DOOH Banner",
  //   toolTip: "Banner advertising displayed on digital screens in public spaces",
  // },
  // {
  //   value: "dooh_video",
  //   label: "DOOH Video",
  //   toolTip: "Video advertising displayed on digital screens in public spaces",
  // },
];

export interface KpiItem {
  name: string;
  value: string | boolean;
  goal_type: string;
  type: "primary" | "secondary" | "tertiary";
  showValue: boolean;
  isCurrency: boolean;
}

export const awareness: ReadonlySet<string> = new Set([
  "reach",
  "inc_reach",
  "viewability",
]);

export const consideration: ReadonlySet<string> = new Set([
  "ctr",
  "cpc",
  "vcr",
  "completion",
]);

export const conversion: ReadonlySet<string> = new Set(["cpa", "roas"]);

export const currencyMetrics: ReadonlySet<string> = new Set([
  "cpa",
  "cpc",
  "cpvc",
  "roas",
]);

export const nonValueMetrics: ReadonlySet<string> = new Set([
  "reach",
  "inc_reach",
]);

export const kpiTypes = [
  { name: "Reach", goal_type: "reach" },
  { name: "CTR", goal_type: "ctr" },
  { name: "CPC", goal_type: "cpc" },

  { name: "CPA", goal_type: "cpa" },
  { name: "ROAS", goal_type: "roas" },
  {
    name: "Viewability",
    goal_type: "viewability",
  },
  {
    name: "VCR",
    goal_type: "vcr",
  },
  {
    name: "Completion Rate",
    goal_type: "vcr",
  },
  {
    name: "Incremental reach",
    goal_type: "inc_reach",
  }
];

export const allKpis = [
  { name: "Reach", showValue: false, isCurrency: false, goal_type: "reach" },
  { name: "Incremental Reach", showValue: false, isCurrency: false, goal_type: "inc_reach" },
  { name: "CTR", showValue: true, isCurrency: false, goal_type: "ctr" },
  { name: "CPC", showValue: true, isCurrency: true, goal_type: "cpc" },
  { name: "CPA", showValue: true, isCurrency: true, goal_type: "cpa" },
  { name: "ROAS", showValue: true, isCurrency: false, goal_type: "roas" },
  { name: "VCR", showValue: true, isCurrency: false, goal_type: "vcr" },
  { name: "Viewability", showValue: true, isCurrency: false, goal_type: "viewability" },
];

export const goalKpi = {
  banner: [
    { name: "Reach", showValue: false, isCurrency: false, goal_type: "reach" },
    { name: "CTR", showValue: true, isCurrency: false, goal_type: "ctr" },
    { name: "CPC", showValue: true, isCurrency: true, goal_type: "cpc" },
    { name: "CPA", showValue: true, isCurrency: true, goal_type: "cpa" },
    { name: "ROAS", showValue: true, isCurrency: false, goal_type: "roas" },
    {
      name: "Viewability",
      showValue: true,
      isCurrency: false,
      goal_type: "viewability",
    },
  ],
  html5: [
    { name: "Reach", showValue: false, isCurrency: false, goal_type: "reach" },
    { name: "CTR", showValue: true, isCurrency: false, goal_type: "ctr" },
    { name: "CPC", showValue: true, isCurrency: true, goal_type: "cpc" },
    { name: "CPA", showValue: true, isCurrency: true, goal_type: "cpa" },
    { name: "ROAS", showValue: true, isCurrency: false, goal_type: "roas" },
    {
      name: "Viewability",
      showValue: true,
      isCurrency: false,
      goal_type: "viewability",
    },
  ],
  video: [
    { name: "Reach", showValue: false, isCurrency: false, goal_type: "reach" },
    {
      name: "VCR",
      showValue: true,
      isCurrency: false,
      goal_type: "vcr",
    },
    { name: "CTR", showValue: true, isCurrency: false, goal_type: "ctr" },
    { name: "CPC", showValue: true, isCurrency: true, goal_type: "cpc" },
    { name: "CPA", showValue: true, isCurrency: true, goal_type: "cpa" },
    { name: "ROAS", showValue: true, isCurrency: false, goal_type: "roas" },
    {
      name: "Viewability",
      showValue: true,
      isCurrency: false,
      goal_type: "viewability",
    },
  ],
  audio: [
    { name: "Reach", showValue: false, isCurrency: false, goal_type: "reach" },
    {
      name: "Completion Rate",
      showValue: true,
      isCurrency: false,
      goal_type: "vcr",
    },
    {
      name: "Viewability",
      showValue: true,
      isCurrency: false,
      goal_type: "viewability",
    },
  ],
  ctv: [
    { name: "Reach", showValue: false, isCurrency: false, goal_type: "reach" },
    {
      name: "Incremental reach",
      showValue: false,
      isCurrency: false,
      goal_type: "inc_reach",
    },
    {
      name: "VCR",
      showValue: true,
      isCurrency: false,
      goal_type: "vcr",
    },
    {
      name: "Viewability",
      showValue: true,
      isCurrency: false,
      goal_type: "viewability",
    },
  ],
  dooh_banner: [
    { name: "Reach", showValue: false, isCurrency: false, goal_type: "reach" },
  ],
  dooh_video: [
    { name: "Reach", showValue: false, isCurrency: false, goal_type: "reach" },
  ],
  native_banner: [
    { name: "Reach", showValue: false, isCurrency: false, goal_type: "reach" },
    { name: "CTR", showValue: true, isCurrency: false, goal_type: "ctr" },
    { name: "CPC", showValue: true, isCurrency: true, goal_type: "cpc" },
    { name: "CPA", showValue: true, isCurrency: true, goal_type: "cpa" },
    { name: "ROAS", showValue: true, isCurrency: false, goal_type: "roas" },
    {
      name: "Viewability",
      showValue: true,
      isCurrency: false,
      goal_type: "viewability",
    },
  ],
  native_video: [
    { name: "Reach", showValue: false, isCurrency: false, goal_type: "reach" },
    {
      name: "VCR",
      showValue: true,
      isCurrency: false,
      goal_type: "vcr",
    },
    { name: "CTR", showValue: true, isCurrency: false, goal_type: "ctr" },
    { name: "CPC", showValue: true, isCurrency: true, goal_type: "cpc" },
    { name: "CPA", showValue: true, isCurrency: true, goal_type: "cpa" },
    { name: "ROAS", showValue: true, isCurrency: false, goal_type: "roas" },
    {
      name: "Viewability",
      showValue: true,
      isCurrency: false,
      goal_type: "viewability",
    },
  ],
};

export interface ioBulkFlightData {
  io_id: number;
  flight: TdspIOFlights;
}

export interface bulkEditFlightData {
  start_date;
  end_date;
  id?;
  disable_start_date: boolean;
  disable_end_date: boolean;
  revenue_budget: number;
  impression_budget: number;
  daily_revenue_budget: number;
  daily_imps_budget: number;
  description: string;
  click_pacing_daily_minimum_budget: number;
  click_pacing_flight_budget: number;
  click_pacing_average_daily_budget?: number;
  click_pacing_clicks_delivered?: number;
  // modify: boolean;
}

export interface ioFlightsRetrivalData {
  io_id: number;
  flights: Array<bulkEditFlightData>;
}
